// import {
//   ROLES_SIXT,
//   useAuth,
// } from 'auth'
import T from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Divider, Link, TextField } from '@mui/material'
import { orderBy } from 'lodash'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import theme from 'theme'
import { differenceInDays } from 'date-fns'
import { Link as RLink } from 'react-router-dom'
import { COUNTRY_CONFIG_MAP } from 'lib/countryConfig'

import Table from 'components/atoms/Table'
import SearchField from 'components/atoms/SearchField'
import ConfirmBar from 'components/atoms/ConfirmBar'
import ConfirmationDialog from 'components/atoms/ConfirmationDialog'

import useEntityData from 'hooks/useEntityData'
import useCarsFilters from 'hooks/useCarsFilters'
import useSort from 'hooks/useSort'

import logger from '../../../logger'
import config from '../../../config'

import { createRepriceTableColumns } from './RepriceTableColumns'

const RepriceTable = () => {
  const { t } = useTranslation()

  const countryConfig = COUNTRY_CONFIG_MAP[config.country ?? 'it']

  const { items: fetchedItems, isFetching } = useEntityData({
    entity: 'inventory/list-for-reprice',
    limit: 1000,
  })

  const [items, setItems] = useState([])
  const [dialogItem, setDialogItem] = useState(null)

  const { order, setSort } = useSort('lastPriceChangedAt')

  const carsFilters = useCarsFilters()

  const { filters, initFilters, setQuery, filterCars } = carsFilters

  const formik = useFormik({
    validationSchema: Yup.object().shape(
      items.reduce((acc, item) => {
        acc[`${item.inventoryId}_newPrice`] = Yup.number()
        return acc
      }, {})
    ),
    initialValues: items.reduce((acc, item) => {
      acc[`${item.inventoryId}_newPrice`] = null
      return acc
    }, {}),
  })

  const onUpdatePrice = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (inventoryId, data) => {
      formik.setSubmitting(true)

      try {
        const result = await axios.post(
          `${config.apiBaseUrl}inventory/update-price`,
          data
        )

        if (result.data.statusCode !== 200) {
          setDialogItem({
            severity: 'error',
            title: t('inventoryReprice.update.error.title'),
            message: t(
              `inventoryReprice.update.error.message${result.data.statusCode}`
            ),
          })
          if ([401, 404].includes(result.data.statusCode)) {
            setItems((s) =>
              s.map((item) => ({
                ...item,
                ...(item.inventoryId === inventoryId
                  ? {
                      formDisabled: true,
                    }
                  : {}),
              }))
            )
          }
        } else {
          setItems((s) =>
            s.map((item) => ({
              ...item,
              ...(item.inventoryId === inventoryId
                ? {
                    price: data.price,
                    fee: data.fee,
                    priceChangeCount: item.priceChangeCount
                      ? item.priceChangeCount + 1
                      : 1,
                  }
                : {}),
            }))
          )

          setDialogItem({
            severity: 'success',
            title: t('inventoryReprice.update.success.title'),
            message: t('inventoryReprice.update.success.message'),
          })
        }
      } catch (e) {
        logger.error('[Error] - reprice update', e)
        setDialogItem({
          severity: 'error',
          title: t('inventoryReprice.update.error.title'),
          message: t('inventoryReprice.update.error.message'),
        })
      }
      formik.setFieldValue(`${inventoryId}_newPrice`, null, true)
      formik.setSubmitting(false)
    },
    [formik, t]
  )

  const readOnly = useMemo(() => isFetching, [isFetching])

  const preparedItems = useMemo(
    () =>
      items.map((item) => {
        return {
          ...item,
          km: Number(item.km),
          suggestedPrice: item.price + item.fee,
          newPrice: (
            <Box width="110px">
              <TextField
                name={`${item.inventoryId}_newPrice`}
                // error={meta.touched && !!meta.error}
                // helperText={meta.touched && meta.error}
                size="small"
                type="number"
                margin="none"
                onChange={formik.handleChange}
                value={formik.values[`${item.inventoryId}_newPrice`] ?? ''}
                disabled={readOnly || item.formDisabled || formik.isSubmitting}
              />
            </Box>
          ),
          update: (
            <Button
              variant="contained"
              data-testid={'inventory-reprice-update-submit'}
              disabled={
                readOnly ||
                !formik?.values?.[`${item.inventoryId}_newPrice`] ||
                formik.isSubmitting
              }
              onClick={() =>
                onUpdatePrice(item.inventoryId, {
                  sessionId: item.sessionId,
                  plate: item.plate,
                  vin: item.vin,
                  price: formik?.values?.[`${item.inventoryId}_newPrice`] ?? 0,
                })
              }
            >
              {t('inventoryReprice.updateButton')}
            </Button>
          ),
        }
      }),
    [
      formik.handleChange,
      formik.isSubmitting,
      formik.values,
      items,
      onUpdatePrice,
      readOnly,
      t,
    ]
  )

  // const sixtUser = useMemo(() => hasOneOfRoles(ROLES_SIXT), [hasOneOfRoles])

  const numberFormatter = useMemo(() => new Intl.NumberFormat('it-IT'), [])

  const filteredItems = useMemo(
    () => filterCars(preparedItems, filters),
    [preparedItems, filterCars, filters]
  )

  const sortedItems = useMemo(
    () => orderBy(filteredItems, [order?.column], [order?.direction]),
    [filteredItems, order?.column, order?.direction]
  )

  const columns = useMemo(
    () =>
      createRepriceTableColumns(carsFilters, countryConfig, numberFormatter, t),
    [carsFilters, countryConfig, numberFormatter, t]
  )

  useEffect(() => {
    if (!items.length || filters.initialized) {
      return
    }

    initFilters(items)
  }, [items, initFilters, filters.initialized])

  useEffect(() => {
    if (!fetchedItems || !fetchedItems.length || items.length) {
      return
    }

    setItems(fetchedItems)
  }, [items, initFilters, fetchedItems])

  return (
    <>
      <Table
        isFetching={isFetching}
        items={sortedItems}
        columns={columns}
        sortable
        excludedSortableIds={[
          'partner',
          'plate',
          'priceList',
          'days',
          'lead',
          'price',
          'newPrice',
          'update',
        ]}
        orderColumn={order?.column}
        orderDirection={order?.direction}
        handleSort={setSort}
        stickyHeader
        bodyMaxHeight={`calc(100vh - ${80 + 20 + 32 + 73 + 20 + 60 + 20}px)`} //mainPaddingTop + pagePaddingTop + title + titleMarginBottom + tableHeader + tableMarginBottom + pagePaddingBottom
        headerProps={{ sx: { px: 1 } }}
        cellProps={{ sx: { px: 1 } }}
        getRowProps={(row) => ({
          sx: {
            ...(differenceInDays(
              new Date(),
              new Date(row.original.lastPriceChangedAt)
            ) < 25
              ? {
                  '&, & [data-sticky-td]': {
                    background: theme.custom.inventoryPriceEarlyChanged,
                  },
                }
              : {}),
            ...(row.original.formDisabled
              ? {
                  '&, & [data-sticky-td], &.MuiTableRow-hover:hover, &.MuiTableRow-hover:hover [data-sticky-td]':
                    {
                      background: theme.custom.disabled,
                    },
                }
              : {}),
          },
        })}
        top={
          <>
            <ConfirmBar
              component="div"
              hideConfirm
              left={
                <>
                  <SearchField
                    data-testid="text-search"
                    placeholder={t('common.form.searchBy')}
                    value={filters.query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </>
              }
              leftProps={{ sx: { width: 0.5 } }}
              right={
                <>
                  <Link component={RLink} to="/partner-inventory">
                    <Button variant="text">
                      {t('inventoryReprice.goToInventory')}
                    </Button>
                  </Link>
                </>
              }
            />
            <Divider />
          </>
        }
      />
      {!!dialogItem && (
        <ConfirmationDialog
          open={!!dialogItem}
          title={t(`inventoryReprice.update.${dialogItem?.severity}.title`)}
          handleClose={() => setDialogItem(null)}
          handleSubmit={() => setDialogItem(null)}
          confirmLabel={t('common.form.close')}
          hideCancel
          content={dialogItem?.message}
          contentProps={{
            color: dialogItem.severity === 'error' ? 'error' : undefined,
          }}
        />
      )}
    </>
  )
}

RepriceTable.propTypes = {
  contentIsReadOnly: T.bool,
}

export default RepriceTable
